// Supabase service layer — same export API as the original firestoreService.js.
// Components and pages import the same hooks/functions; nothing else changes.
import { useEffect, useState, useMemo } from "react";
import { supabase, supabaseReady } from "@/lib/firebase";
import { PRODUCT_SEED } from "@/lib/seedData";

// Unique channel names prevent subscription conflicts across hook instances
const ch = (prefix) => `${prefix}-${Math.random().toString(36).slice(2, 7)}`;

// Supabase returns plain ISO date strings; UI components were written
// against Firestore's { seconds } Timestamp shape. These shims add the
// fields the UI expects without having to touch every render call site.
const toFsTs = (iso) => (iso ? { seconds: Math.floor(new Date(iso).getTime() / 1000) } : null);

function normalizeOrder(row) {
  if (!row) return row;
  const history = Array.isArray(row.status_history) ? row.status_history : [];
  return {
    ...row,
    createdAt: toFsTs(row.created_at),
    updatedAt: toFsTs(row.updated_at),
    utrSubmittedAt: toFsTs(row.utr_submitted_at),
    statusHistory: history,
    paymentConfirmed: history.some((h) => h.status === "payment_confirmed"),
    order_source: row.order_source || "website",
    fulfillment_type: row.fulfillment_type || "courier",
    payment_method: row.payment_method || null,
  };
}

function normalizeTimestamped(row) {
  return row ? { ...row, createdAt: toFsTs(row.created_at) } : row;
}

function normalizeMessage(row) {
  return row ? { ...row, at: toFsTs(row.created_at) } : row;
}

// ─── PRODUCTS ────────────────────────────────────────────────────────────────
export function useProducts() {
  const [products, setProducts] = useState(
    () => [...PRODUCT_SEED].sort((a, b) => a.sort - b.sort)
  );

  useEffect(() => {
    if (!supabaseReady) return;

    supabase
      .from("products")
      .select("*")
      .order("sort", { ascending: true })
      .then(({ data }) => { if (data?.length) setProducts(data); });

    const channel = supabase
      .channel(ch("products"))
      .on("postgres_changes", { event: "*", schema: "public", table: "products" }, (p) => {
        setProducts((prev) => {
          const sorted = (list) => [...list].sort((a, b) => a.sort - b.sort);
          if (p.eventType === "INSERT") return sorted([...prev, p.new]);
          if (p.eventType === "UPDATE") return sorted(prev.map((r) => r.id === p.new.id ? p.new : r));
          if (p.eventType === "DELETE") return prev.filter((r) => r.id !== p.old.id);
          return prev;
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return products;
}

export async function seedProductsToFirestore() {
  if (!supabaseReady) throw new Error("Supabase not configured");
  // Defaults spread first, then overridden per-row — a bulk upsert sends an
  // explicit NULL for any column a row omits if other rows in the same
  // batch include it, which breaks NOT NULL columns like `featured`. This
  // guarantees every row has the same keys regardless of what PRODUCT_SEED
  // does or doesn't specify.
  const rows = PRODUCT_SEED.map((p) => ({ featured: false, in_stock: true, stock_note: null, ...p }));
  const { error } = await supabase.from("products").upsert(rows, { onConflict: "id" });
  if (error) throw error;
}

export async function saveProduct(product) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const id = product.id || product.slug;
  const { error } = await supabase
    .from("products")
    .upsert({ ...product, id, updated_at: new Date().toISOString() }, { onConflict: "id" });
  if (error) throw error;
  return id;
}

export async function deleteProduct(id) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase.from("products").delete().eq("id", id);
  if (error) throw error;
}

// ─── ORDERS ──────────────────────────────────────────────────────────────────
// Resolves to { id, order_number } — id is the row's UUID primary key,
// order_number is the human-readable AHP2802<DDMMYY><seq> number (migration
// 029). Older orders created before that migration have order_number: null.
export async function createOrder(orderData) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  // Server-side RPC (migration 023, extended in 030 for combo/sample-pack
  // items) recalculates prices from live product data / admin-set tier and
  // pack prices, validates stock + pincode + offer codes atomically, and
  // sets uid from auth.uid() — client-supplied totals are never trusted.
  const items = (orderData.items || []).map((it) => {
    if (it.item_type === "combo") {
      return { item_type: "combo", combo_type: it.combo_type, quantity: it.quantity, selections: it.selections };
    }
    if (it.item_type === "sample_pack") {
      return { item_type: "sample_pack", pack_type: it.pack_type, quantity: it.quantity };
    }
    return { product_id: it.product_id, size: it.size, quantity: it.quantity };
  });
  const { data, error } = await supabase.rpc("create_checkout_order", {
    p_items:         items,
    p_customer_name: orderData.customer_name,
    p_phone:         orderData.phone,
    p_email:         orderData.email         || null,
    p_address:       orderData.address,
    p_city:          orderData.city,
    p_state:         orderData.state,
    p_pincode:       orderData.pincode,
    p_country:       orderData.country,
    p_notes:         orderData.notes         || null,
    p_offer_code:    orderData.offer_code    || null,
    p_fulfillment_type: orderData.fulfillment_type || "courier",
  });
  if (error) throw error;
  return data;
}

// Resolves to { id, total } — id is a pending_checkouts row, NOT a real
// order yet. Used for the PhonePe flow: the real order is only created once
// the gateway confirms payment (see finalize_pending_checkout). Same param
// shape as createOrder above, so it can reuse the exact same item mapping.
export async function createPendingCheckout(orderData) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const items = (orderData.items || []).map((it) => {
    if (it.item_type === "combo") {
      return { item_type: "combo", combo_type: it.combo_type, quantity: it.quantity, selections: it.selections };
    }
    if (it.item_type === "sample_pack") {
      return { item_type: "sample_pack", pack_type: it.pack_type, quantity: it.quantity };
    }
    return { product_id: it.product_id, size: it.size, quantity: it.quantity };
  });
  const { data, error } = await supabase.rpc("create_pending_checkout", {
    p_items:         items,
    p_customer_name: orderData.customer_name,
    p_phone:         orderData.phone,
    p_email:         orderData.email         || null,
    p_address:       orderData.address,
    p_city:          orderData.city,
    p_state:         orderData.state,
    p_pincode:       orderData.pincode,
    p_country:       orderData.country,
    p_notes:         orderData.notes         || null,
    p_offer_code:    orderData.offer_code    || null,
    p_fulfillment_type: orderData.fulfillment_type || "courier",
  });
  if (error) throw error;
  return data;
}

// Resolves to { id, order_number } — see createOrder above.
export async function createStoreOrder({ items, customer_name, phone, payment_method }) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { data, error } = await supabase.rpc("create_store_order", {
    p_items:          items,
    p_customer_name:  customer_name,
    p_phone:          phone || null,
    p_payment_method: payment_method,
  });
  if (error) throw error;
  return data;
}

export function useMyOrders(uid) {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!supabaseReady || !uid) { setOrders([]); return; }

    supabase
      .from("orders")
      .select("*")
      .eq("uid", uid)
      .order("created_at", { ascending: false })
      .then(({ data }) => setOrders((data || []).map(normalizeOrder)));

    const channel = supabase
      .channel(ch("my-orders"))
      .on("postgres_changes", {
        event: "*", schema: "public", table: "orders", filter: `uid=eq.${uid}`,
      }, (p) => {
        setOrders((prev) => {
          if (!prev) return prev;
          const row = normalizeOrder(p.new);
          if (p.eventType === "INSERT") return [row, ...prev];
          if (p.eventType === "UPDATE") return prev.map((o) => o.id === row.id ? row : o);
          if (p.eventType === "DELETE") return prev.filter((o) => o.id !== p.old.id);
          return prev;
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [uid]);

  return orders;
}

export function useAllOrders() {
  const [orders, setOrders] = useState(null);

  useEffect(() => {
    if (!supabaseReady) { setOrders([]); return; }

    supabase
      .from("orders")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data }) => setOrders((data || []).map(normalizeOrder)));

    const channel = supabase
      .channel(ch("all-orders"))
      .on("postgres_changes", { event: "*", schema: "public", table: "orders" }, (p) => {
        setOrders((prev) => {
          if (!prev) return prev;
          const row = normalizeOrder(p.new);
          if (p.eventType === "INSERT") return [row, ...prev];
          if (p.eventType === "UPDATE") return prev.map((o) => o.id === row.id ? row : o);
          if (p.eventType === "DELETE") return prev.filter((o) => o.id !== p.old.id);
          return prev;
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return orders;
}

// Fetches by exact id via a SECURITY DEFINER RPC. This path is for signed-in
// owners/admins only; public tracking uses lookupOrderForTracking() with
// order reference + contact verification.
export function useOrder(id) {
  const [order, setOrder] = useState(undefined);

  useEffect(() => {
    if (!supabaseReady || !id) { setOrder(null); return; }

    supabase
      .rpc("get_order_by_id", { p_id: id })
      .then(({ data, error }) => setOrder(error || !data?.length ? null : normalizeOrder(data[0])));

    const channel = supabase
      .channel(ch(`order-${id}`))
      .on("postgres_changes", {
        event: "*", schema: "public", table: "orders", filter: `id=eq.${id}`,
      }, (p) => {
        setOrder(p.eventType === "DELETE" ? null : normalizeOrder(p.new));
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [id]);

  return order;
}

// Public tracking lookup: requires BOTH an order reference and customer
// contact (email or phone). Returns null when the pair doesn't match.
export async function lookupOrderForTracking({ orderRef, contact }) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { data, error } = await supabase.rpc("get_order_by_ref_and_contact", {
    p_order_ref: orderRef,
    p_contact: contact,
  });
  if (error) throw error;
  return data?.[0] ? normalizeOrder(data[0]) : null;
}

export async function updateOrderStatus(orderId, status, note = "") {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase.rpc("append_order_status", {
    p_order_id: orderId,
    p_status:   status,
    p_note:     note,
  });
  if (error) throw error;
}

export async function updateOrderCourier(orderId, courier) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase.from("orders").update({ courier }).eq("id", orderId);
  if (error) throw error;
}

// Submits or edits a payment UTR/reference number. Goes through an RPC
// (not a direct table update) because RLS only allows admins to update
// orders — this lets the order's owner (including guests) do it too,
// while the database itself enforces the 5-minute edit window.
export async function submitOrderUtr(orderId, utr) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { data, error } = await supabase.rpc("submit_order_utr", { p_order_id: orderId, p_utr: utr });
  if (error) throw error;
  return data?.[0] ? normalizeOrder(data[0]) : null;
}

// Calls the phonepe-initiate Edge Function to start a PhonePe payment for an
// existing order. The function fetches the order amount from the DB (not
// trusted from the client). Returns { redirectUrl } — the caller must
// redirect window.location to that URL to open the PhonePe payment page.
export async function initiatePhonePePayment(orderId) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const redirectBaseUrl = window.location.origin;
  const { data, error } = await supabase.functions.invoke("phonepe-initiate", {
    body: { orderId, redirectBaseUrl },
  });
  if (error) throw error;
  if (!data?.redirectUrl) throw new Error("No redirect URL returned from payment gateway");
  return data.redirectUrl;
}

// ─── CONTACT MESSAGES ────────────────────────────────────────────────────────
export async function submitContactMessage(payload) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  // No .select() here: only admins can read contact_messages rows (RLS), so
  // asking Postgrest to return the inserted row would fail for the sender.
  const { error } = await supabase.from("contact_messages").insert({ ...payload, read: false });
  if (error) throw error;
}

export async function deleteContactMessage(id) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  // .select() after delete so an RLS no-op (e.g. non-super-admin, expired
  // session) surfaces as a real error instead of a silent false "success".
  const { data, error } = await supabase.from("contact_messages").delete().eq("id", id).select("id");
  if (error) throw error;
  if (!data || data.length === 0) throw new Error("Message not found or you don't have permission to delete it");
}

export function useContactMessages() {
  const [list, setList] = useState(null);

  useEffect(() => {
    if (!supabaseReady) { setList([]); return; }

    supabase
      .from("contact_messages")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data }) => setList((data || []).map(normalizeTimestamped)));

    const channel = supabase
      .channel(ch("contact"))
      .on("postgres_changes", { event: "*", schema: "public", table: "contact_messages" }, (p) => {
        setList((prev) => {
          if (!prev) return prev;
          const row = normalizeTimestamped(p.new);
          if (p.eventType === "INSERT") return [row, ...prev];
          if (p.eventType === "UPDATE") return prev.map((m) => m.id === row.id ? row : m);
          if (p.eventType === "DELETE") return prev.filter((m) => m.id !== p.old.id);
          return prev;
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return list;
}

// ─── CUSTOMERS ───────────────────────────────────────────────────────────────
export function useAllCustomers() {
  const [list, setList] = useState(null);

  useEffect(() => {
    if (!supabaseReady) { setList([]); return; }

    supabase
      .from("customers")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data }) => setList((data || []).map(normalizeTimestamped)));

    const channel = supabase
      .channel(ch("customers"))
      .on("postgres_changes", { event: "*", schema: "public", table: "customers" }, (p) => {
        setList((prev) => {
          if (!prev) return prev;
          if (p.eventType === "INSERT") return [normalizeTimestamped(p.new), ...prev];
          if (p.eventType === "UPDATE") return prev.map((c) => c.id === p.new.id ? normalizeTimestamped(p.new) : c);
          if (p.eventType === "DELETE") return prev.filter((c) => c.id !== p.old.id);
          return prev;
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return list;
}

export async function updateCustomer(id, data) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase.from("customers").update(data).eq("id", id);
  if (error) throw error;
}

// Hard-deletes the customer's Supabase Auth account via an Edge Function
// (needs the service-role key, so it can't run from the browser directly).
// No email/notification is sent to the customer.
export async function createStoreAssociate({ name, store_user_id, password }) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { data, error } = await supabase.functions.invoke("admin-create-store-associate", {
    body: { name, store_user_id, password },
  });
  if (error) {
    let msg = error.message;
    try {
      const body = await error.context?.json();
      if (body?.error) msg = body.error;
    } catch {}
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

export async function updateStoreAssociate({ id, name, store_user_id, password }) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const body = { id, name, store_user_id };
  if (password) body.password = password;
  const { data, error } = await supabase.functions.invoke("admin-update-store-associate", { body });
  if (error) {
    let msg = error.message;
    try {
      const resp = await error.context?.json();
      if (resp?.error) msg = resp.error;
    } catch {}
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  return data;
}

// Uploads a (already client-compressed) product image to Cloudflare R2 via
// an admin-only Edge Function — R2 credentials can never ship to the
// browser, so the actual upload happens server-side. Returns the new
// image's public R2.dev URL.
export async function uploadProductImage(file, productSlug = null) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const dataBase64 = await fileToBase64(file);
  const body = { filename: file.name, contentType: file.type, dataBase64 };
  if (productSlug) body.productSlug = productSlug;
  const { data, error } = await supabase.functions.invoke("admin-upload-product-image", { body });
  if (error) {
    let msg = error.message;
    try {
      const body = await error.context?.json();
      if (body?.error) msg = body.error;
    } catch {}
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
  return data.url;
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1] || "");
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export async function deleteCustomer(id) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { data, error } = await supabase.functions.invoke("admin-delete-customer", {
    body: { customerId: id },
  });
  if (error) {
    let msg = error.message;
    try {
      const body = await error.context?.json();
      if (body?.error) msg = body.error;
    } catch {}
    throw new Error(msg);
  }
  if (data?.error) throw new Error(data.error);
}

// ─── ORDER MESSAGES ──────────────────────────────────────────────────────────
export function useOrderMessages(orderId) {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (!supabaseReady || !orderId) return;

    // Via RPC (not a direct table select) so guest order owners can read
    // their own thread too — RLS requires orders.uid = auth.uid(), which
    // never matches for guest orders since both sides are NULL.
    supabase
      .rpc("get_order_messages", { p_order_id: orderId })
      .then(({ data }) => setMessages((data || []).map(normalizeMessage)));

    const channel = supabase
      .channel(ch(`order-msgs-${orderId}`))
      .on("postgres_changes", {
        event: "*", schema: "public", table: "order_messages",
        filter: `order_id=eq.${orderId}`,
      }, (p) => {
        setMessages((prev) => {
          const row = normalizeMessage(p.new);
          if (p.eventType === "INSERT") return [...prev, row];
          if (p.eventType === "UPDATE") return prev.map((m) => m.id === row.id ? row : m);
          if (p.eventType === "DELETE") return prev.filter((m) => m.id !== p.old.id);
          return prev;
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [orderId]);

  return messages;
}

export async function sendOrderMessage(orderId, { from, sender, text }) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase
    .from("order_messages")
    .insert({ order_id: orderId, from, sender, text: text.trim(), read: false });
  if (error) throw error;
}

// Guest checkout orders (uid IS NULL) can't INSERT via RLS — this RPC mirrors
// get_order_messages: knowing the order UUID is the capability token.
export async function sendGuestOrderMessage(orderId, { sender, text }) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase.rpc("send_guest_order_message", {
    p_order_id: orderId,
    p_sender:   sender,
    p_text:     text,
  });
  if (error) throw error;
}

// ─── CHECKOUT SETTINGS ───────────────────────────────────────────────────────
// Same defaults are seeded server-side in migrations 050/051 — kept in sync
// by hand, there's no shared source of truth between SQL and JS here.
export const DEFAULT_STATE_SHIPPING_RATES = {
  "Andhra Pradesh": 60, "Telangana": 60,
  "Tamil Nadu": 80, "Karnataka": 80, "Kerala": 80,
  "Maharashtra": 100, "Goa": 100,
  "Gujarat": 120, "Delhi": 120, "Uttar Pradesh": 120, "West Bengal": 120,
  "Rajasthan": 120, "Madhya Pradesh": 120, "Bihar": 120, "Punjab": 120,
  "Haryana": 120, "Odisha": 120,
  "Assam": 140, "Jammu & Kashmir": 140,
  "Other States": 120,
};

export const DEFAULT_CHECKOUT_SETTINGS = {
  gstPercent: 5,
  stateShippingRates: DEFAULT_STATE_SHIPPING_RATES,
  enableGuestCheckout: true,
  enableQrCheckout: true,
  enablePickup: true,
  enableCourier: true,
  enableStoreUpi: true,
  messageCooldownMinutes: 2,
  restrictDelivery: false,
  serviceablePincodes: [],
  pickupAddress: {
    name: "Annapurna Home Pickles",
    address: "Rajahmundry, East Godavari District",
    city: "Rajahmundry",
    state: "Andhra Pradesh",
    pincode: "533101",
    note: "Collect when we notify you — usually 2–5 working days after your order is ready.",
  },
  courierNote: "Shipped via courier partner. Not instant delivery — usually 3–7 working days after dispatch.",
};

function parseCheckout(data) {
  if (!data) return DEFAULT_CHECKOUT_SETTINGS;
  return {
    gstPercent:          data.gstPercent          ?? DEFAULT_CHECKOUT_SETTINGS.gstPercent,
    // Merge rather than replace so a newly-added state in a future release
    // still gets a rate even on installs whose settings row predates it.
    stateShippingRates:  { ...DEFAULT_STATE_SHIPPING_RATES, ...(data.stateShippingRates || {}) },
    enableGuestCheckout: data.enableGuestCheckout  ?? DEFAULT_CHECKOUT_SETTINGS.enableGuestCheckout,
    enableQrCheckout:    data.enableQrCheckout    ?? DEFAULT_CHECKOUT_SETTINGS.enableQrCheckout,
    enablePickup:        data.enablePickup        ?? DEFAULT_CHECKOUT_SETTINGS.enablePickup,
    enableCourier:       data.enableCourier       ?? DEFAULT_CHECKOUT_SETTINGS.enableCourier,
    enableStoreUpi:      data.enableStoreUpi      ?? DEFAULT_CHECKOUT_SETTINGS.enableStoreUpi,
    messageCooldownMinutes: data.messageCooldownMinutes ?? DEFAULT_CHECKOUT_SETTINGS.messageCooldownMinutes,
    restrictDelivery: data.restrictDelivery ?? DEFAULT_CHECKOUT_SETTINGS.restrictDelivery,
    serviceablePincodes: Array.isArray(data.serviceablePincodes) ? data.serviceablePincodes : [],
    pickupAddress: { ...DEFAULT_CHECKOUT_SETTINGS.pickupAddress, ...(data.pickupAddress || {}) },
    courierNote: data.courierNote ?? DEFAULT_CHECKOUT_SETTINGS.courierNote,
  };
}

// True if a pincode is serviceable given the admin's configured list — each
// entry can be an exact 6-digit pincode or a shorter prefix (e.g. "533"
// matches every pincode in that area). Restriction is a no-op when disabled
// or no codes have been configured yet, so it can't accidentally block every
// checkout the moment someone turns the toggle on before adding codes.
export function isPincodeServiceable(pincode, settings) {
  if (!settings?.restrictDelivery) return true;
  const list = settings.serviceablePincodes || [];
  if (list.length === 0) return true;
  const p = (pincode || "").trim();
  return list.some(code => p.startsWith(code.trim()));
}

export function useCheckoutSettings() {
  const [settings, setSettings] = useState(DEFAULT_CHECKOUT_SETTINGS);

  useEffect(() => {
    if (!supabaseReady) return;

    supabase
      .from("settings").select("data").eq("name", "checkout").single()
      // Always set a *new* object once this resolves, even when no row
      // exists yet — consumers compare against the DEFAULT_CHECKOUT_SETTINGS
      // reference to tell "still loading" apart from "loaded, here are the
      // real values," and parseCheckout(null) would otherwise return that
      // exact same default reference, leaving them stuck thinking it's
      // still loading.
      .then(({ data }) => setSettings(data?.data ? parseCheckout(data.data) : { ...DEFAULT_CHECKOUT_SETTINGS }));

    const channel = supabase
      .channel(ch("checkout"))
      .on("postgres_changes", {
        event: "*", schema: "public", table: "settings", filter: "name=eq.checkout",
      }, (p) => { if (p.new?.data) setSettings(parseCheckout(p.new.data)); })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return settings;
}

export async function updateCheckoutSettings(data) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase
    .from("settings")
    .upsert({ name: "checkout", data }, { onConflict: "name" });
  if (error) throw error;
}

// ─── CUSTOM COMBO BUILDER (admin-configured tiers, per-product price) ───────
// Different pickles cost different amounts to make, so there's no single
// flat price per tier — productPrices maps each eligible product's id to its
// own admin-set price, and the combo total is the sum of whichever products
// the customer actually selects (see create_checkout_order, migration 030).
export const DEFAULT_COMBO_SETTINGS = {
  enabled: false,
  tiers: {
    combo:        { label: "Combo",        itemCount: 2, productPrices: {} },
    super_combo:  { label: "Super Combo",  itemCount: 3, productPrices: {} },
    mega_combo:   { label: "Mega Combo",   itemCount: 4, productPrices: {} },
    ultra_combo:  { label: "Ultra Combo",  itemCount: 5, productPrices: {} },
  },
  packSizes: ["100g", "200g", "250g", "500g"],
};

function parseComboSettings(data) {
  if (!data) return DEFAULT_COMBO_SETTINGS;
  return {
    enabled: data.enabled ?? false,
    tiers: {
      combo:       { ...DEFAULT_COMBO_SETTINGS.tiers.combo,       ...(data.tiers?.combo       || {}) },
      super_combo: { ...DEFAULT_COMBO_SETTINGS.tiers.super_combo, ...(data.tiers?.super_combo || {}) },
      mega_combo:  { ...DEFAULT_COMBO_SETTINGS.tiers.mega_combo,  ...(data.tiers?.mega_combo  || {}) },
      ultra_combo: { ...DEFAULT_COMBO_SETTINGS.tiers.ultra_combo, ...(data.tiers?.ultra_combo || {}) },
    },
    packSizes: Array.isArray(data.packSizes) && data.packSizes.length ? data.packSizes : DEFAULT_COMBO_SETTINGS.packSizes,
  };
}

export function useComboSettings() {
  const [settings, setSettings] = useState(DEFAULT_COMBO_SETTINGS);

  useEffect(() => {
    if (!supabaseReady) return;

    supabase
      .from("settings").select("data").eq("name", "comboSettings").single()
      .then(({ data }) => setSettings(data?.data ? parseComboSettings(data.data) : { ...DEFAULT_COMBO_SETTINGS }));

    const channel = supabase
      .channel(ch("comboSettings"))
      .on("postgres_changes", {
        event: "*", schema: "public", table: "settings", filter: "name=eq.comboSettings",
      }, (p) => { if (p.new?.data) setSettings(parseComboSettings(p.new.data)); })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return settings;
}

export async function updateComboSettings(data) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase
    .from("settings")
    .upsert({ name: "comboSettings", data }, { onConflict: "name" });
  if (error) throw error;
}

// ─── SAMPLE PICKLE PACKS (admin-curated, fixed contents, capped quantity) ───
// Same per-product pricing reasoning as combos — every product in a pack has
// its own admin-set price, and the pack total is the sum of ALL of them
// (the customer can't deselect any, unlike a combo).
export const DEFAULT_SAMPLE_PACK_SETTINGS = {
  enabled: false,
  packs: {
    veg:     { label: "Veg Order Sample Pack",     productPrices: {}, maxQtyPerOrder: 3, fixedSizeLabel: "150g" },
    non_veg: { label: "Non-Veg Order Sample Pack", productPrices: {}, maxQtyPerOrder: 3, fixedSizeLabel: "150g" },
  },
};

function parseSamplePackSettings(data) {
  if (!data) return DEFAULT_SAMPLE_PACK_SETTINGS;
  return {
    enabled: data.enabled ?? false,
    packs: {
      veg:     { ...DEFAULT_SAMPLE_PACK_SETTINGS.packs.veg,     ...(data.packs?.veg     || {}) },
      non_veg: { ...DEFAULT_SAMPLE_PACK_SETTINGS.packs.non_veg, ...(data.packs?.non_veg || {}) },
    },
  };
}

export function useSamplePackSettings() {
  const [settings, setSettings] = useState(DEFAULT_SAMPLE_PACK_SETTINGS);

  useEffect(() => {
    if (!supabaseReady) return;

    supabase
      .from("settings").select("data").eq("name", "samplePackSettings").single()
      .then(({ data }) => setSettings(data?.data ? parseSamplePackSettings(data.data) : { ...DEFAULT_SAMPLE_PACK_SETTINGS }));

    const channel = supabase
      .channel(ch("samplePackSettings"))
      .on("postgres_changes", {
        event: "*", schema: "public", table: "settings", filter: "name=eq.samplePackSettings",
      }, (p) => { if (p.new?.data) setSettings(parseSamplePackSettings(p.new.data)); })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return settings;
}

export async function updateSamplePackSettings(data) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase
    .from("settings")
    .upsert({ name: "samplePackSettings", data }, { onConflict: "name" });
  if (error) throw error;
}

export async function saveRemoteConfig(data) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase
    .from("settings")
    .upsert({ name: "remoteConfig", data }, { onConflict: "name" });
  if (error) throw error;
}

// ─── SITE CONTENT (admin-editable marketing copy, English only) ──────────────
// Each component falls back to its hardcoded English default when a field
// is blank/missing, so this only ever overrides — never blanks out — copy.
export const DEFAULT_SITE_CONTENT = {
  hero_headline_1: "Authentic Andhra",
  hero_headline_2: "Home Pickles",
  hero_headline_3: "Delivered Fresh",
  hero_subhead: "Experience the rich taste of homemade pickles prepared with traditional recipes, hand-pounded spices, and premium ingredients — just like Amma used to make.",
  about_h1: "Traditional flavors,",
  about_h2: "preserved with love.",
  about_body: "When we started this business, it wasn't just about creating a company—it was about creating a future.\n\nAs parents, our greatest dream has always been to provide our children with opportunities, security, and a legacy they can be proud of. We believed that through hard work, integrity, and a commitment to excellence, we could build something meaningful not only for our family but also for the communities we serve.\n\nEvery challenge we've faced has strengthened our purpose. Every customer who places their trust in us becomes part of our journey. Your support helps us continue building a business founded on honesty, quality, and genuine care.\n\nOur mission goes beyond delivering exceptional products and services. We strive to create lasting relationships, make a positive impact, and inspire our children by showing them that success is earned through dedication, perseverance, and doing the right thing.\n\nThank you for being part of our story. Together, we're building more than a business—we're building a legacy for future generations.",
  why_h1: "Six promises in",
  why_h2: "every jar",
  banners: [],
};

export function useSiteContent() {
  const [content, setContent] = useState(DEFAULT_SITE_CONTENT);

  useEffect(() => {
    if (!supabaseReady) return;

    supabase
      .from("settings").select("data").eq("name", "siteContent").single()
      // Same reasoning as useCheckoutSettings — always produce a new object
      // once resolved, even with no row yet, so consumers can tell "loaded"
      // apart from "still loading" via reference inequality.
      .then(({ data }) => setContent({ ...DEFAULT_SITE_CONTENT, ...(data?.data || {}) }));

    const channel = supabase
      .channel(ch("site-content"))
      .on("postgres_changes", {
        event: "*", schema: "public", table: "settings", filter: "name=eq.siteContent",
      }, (p) => { if (p.new?.data) setContent({ ...DEFAULT_SITE_CONTENT, ...p.new.data }); })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return content;
}

export async function saveSiteContent(data) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase
    .from("settings")
    .upsert({ name: "siteContent", data }, { onConflict: "name" });
  if (error) throw error;
}

// ─── POLICY PAGE CONTENT (super-admin-editable legal text) ───────────────────
// One key per policy page. A blank/missing value means "no override" — the
// page renders its original hardcoded legal text (PolicyPages.jsx) exactly as
// before. Only once a super admin types something and saves does that page
// switch to rendering the saved text (via the markdown-lite parser in
// lib/policyMarkdown.js) — same "never blanks out existing copy" convention
// as DEFAULT_SITE_CONTENT above.
export const DEFAULT_POLICY_CONTENT = {
  terms: "",
  privacy: "",
  shipping: "",
  refund: "",
  return: "",
  cancellation: "",
};

export function usePolicyContent() {
  const [content, setContent] = useState(DEFAULT_POLICY_CONTENT);

  useEffect(() => {
    if (!supabaseReady) return;

    supabase
      .from("settings").select("data").eq("name", "policyContent").single()
      .then(({ data }) => setContent({ ...DEFAULT_POLICY_CONTENT, ...(data?.data || {}) }));

    const channel = supabase
      .channel(ch("policy-content"))
      .on("postgres_changes", {
        event: "*", schema: "public", table: "settings", filter: "name=eq.policyContent",
      }, (p) => { if (p.new?.data) setContent({ ...DEFAULT_POLICY_CONTENT, ...p.new.data }); })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return content;
}

export async function savePolicyContent(data) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase
    .from("settings")
    .upsert({ name: "policyContent", data }, { onConflict: "name" });
  if (error) throw error;
}

// ─── ADMIN CHAT ──────────────────────────────────────────────────────────────
export function useAdminChat() {
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (!supabaseReady) return;

    supabase
      .from("admin_chat").select("*").order("created_at", { ascending: true })
      .then(({ data }) => setMessages((data || []).map(normalizeMessage)));

    const channel = supabase
      .channel(ch("admin-chat"))
      .on("postgres_changes", { event: "*", schema: "public", table: "admin_chat" }, (p) => {
        setMessages((prev) => {
          const row = normalizeMessage(p.new);
          if (p.eventType === "INSERT") return [...prev, row];
          if (p.eventType === "UPDATE") return prev.map((m) => m.id === row.id ? row : m);
          if (p.eventType === "DELETE") return prev.filter((m) => m.id !== p.old.id);
          return prev;
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return messages;
}

export async function sendAdminChatMessage(msg) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase.from("admin_chat").insert(msg);
  if (error) throw error;
}

// ─── COST SHEET ──────────────────────────────────────────────────────────────
export const DEFAULT_COST_SHEET = [
  { item: "Chicken",      "250gms": { making: 175, packing: 50, selling: 335  }, "500gms": { making: 350, packing: 75, selling: 665  }, "1000gms": { making: 700,  packing: 100, selling: 1270 } },
  { item: "Prawn",        "250gms": { making: 175, packing: 50, selling: 335  }, "500gms": { making: 350, packing: 75, selling: 665  }, "1000gms": { making: 700,  packing: 100, selling: 1270 } },
  { item: "Mutton",       "250gms": { making: 350, packing: 50, selling: 749  }, "500gms": { making: 700, packing: 75, selling: 1300 }, "1000gms": { making: 1400, packing: 100, selling: 2449 } },
  { item: "Ginger",       "250gms": { making: 70,  packing: 50, selling: 165  }, "500gms": { making: 140, packing: 75, selling: 330  }, "1000gms": { making: 560,  packing: 100, selling: 650  } },
  { item: "Ulli Avakaye", "250gms": { making: 120, packing: 50, selling: 210  }, "500gms": { making: 225, packing: 75, selling: 450  }, "1000gms": { making: 450,  packing: 100, selling: 849  } },
];

export function useCostSheet() {
  const [sheet, setSheet] = useState(DEFAULT_COST_SHEET);

  useEffect(() => {
    if (!supabaseReady) return;

    supabase
      .from("settings").select("data").eq("name", "costSheet").single()
      .then(({ data }) => { if (data?.data?.rows?.length) setSheet(data.data.rows); });

    const channel = supabase
      .channel(ch("cost-sheet"))
      .on("postgres_changes", {
        event: "*", schema: "public", table: "settings", filter: "name=eq.costSheet",
      }, (p) => { if (p.new?.data?.rows?.length) setSheet(p.new.data.rows); })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return sheet;
}

export async function saveCostSheet(rows) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase
    .from("settings")
    .upsert({ name: "costSheet", data: { rows } }, { onConflict: "name" });
  if (error) throw error;
}

function normSizeKey(label) {
  return (label || "").replace(/^(\d+)\s*gms?$/i, (_, n) => `${n}gms`);
}

export async function syncProductPricesFromCostSheet(rows) {
  if (!supabaseReady) throw new Error("Supabase not configured");

  const { data: products, error } = await supabase.from("products").select("*");
  if (error) throw error;

  let updated = 0;
  for (const product of products) {
    const nameLower = (product.name || "").toLowerCase();
    const row = rows.find((r) => nameLower.includes(r.item.toLowerCase()));
    if (!row) continue;

    const newSizes = (product.sizes || []).map((sz) => {
      const key = normSizeKey(sz.label);
      const selling = row[key]?.selling;
      // A cost-sheet cell left at 0/blank means "not entered yet", not
      // "this item is free" — never let an unfilled row zero out a real
      // product price that's already live.
      return selling != null && selling > 0 ? { ...sz, price: selling } : sz;
    });

    if (!newSizes.some((s, i) => s.price !== (product.sizes[i]?.price))) continue;

    await supabase.from("products").update({ sizes: newSizes }).eq("id", product.id);
    updated++;
  }

  return updated;
}

// ─── NOTIFICATIONS ───────────────────────────────────────────────────────────
export function useNotifications() {
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    if (!supabaseReady) return;

    supabase
      .from("notifications").select("*").order("created_at", { ascending: false })
      .then(({ data }) => setNotifs((data || []).map(normalizeTimestamped)));

    const channel = supabase
      .channel(ch("notifs"))
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, (p) => {
        setNotifs((prev) => {
          const row = normalizeTimestamped(p.new);
          if (p.eventType === "INSERT") return [row, ...prev];
          if (p.eventType === "UPDATE") return prev.map((n) => n.id === row.id ? row : n);
          if (p.eventType === "DELETE") return prev.filter((n) => n.id !== p.old.id);
          return prev;
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return notifs;
}

export async function createNotification(data) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase.from("notifications").insert(data);
  if (error) throw error;
}

export async function deleteNotification(id) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase.from("notifications").delete().eq("id", id);
  if (error) throw error;
}

// ─── REVIEWS ─────────────────────────────────────────────────────────────────
// Reviews this customer has already submitted, keyed by order_id, so the
// "My Orders" page can show "Reviewed" instead of a Review button.
export function useMyReviews(uid) {
  const [byOrderId, setByOrderId] = useState({});

  useEffect(() => {
    if (!supabaseReady || !uid) { setByOrderId({}); return; }

    supabase
      .from("reviews")
      .select("*")
      .eq("uid", uid)
      .then(({ data }) => {
        const map = {};
        (data || []).forEach((r) => { map[r.order_id] = r; });
        setByOrderId(map);
      });

    const channel = supabase
      .channel(ch("my-reviews"))
      .on("postgres_changes", {
        event: "INSERT", schema: "public", table: "reviews", filter: `uid=eq.${uid}`,
      }, (p) => {
        setByOrderId((prev) => ({ ...prev, [p.new.order_id]: p.new }));
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [uid]);

  return byOrderId;
}

export async function submitReview({ orderId, uid, customerName, rating, feedback, productId, productName }) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const base = {
    order_id: orderId,
    uid,
    customer_name: customerName,
    rating,
    feedback: feedback?.trim() || null,
  };
  const { error } = await supabase.from("reviews").insert({ ...base, product_id: productId || null, product_name: productName || null });
  if (!error) return;
  // Falls back to the pre-021 shape so review submission — a previously
  // working flow — can never break just because that migration hasn't run
  // yet; it only means product attribution is silently skipped this time.
  if (error.code === "PGRST204") {
    const { error: fallbackError } = await supabase.from("reviews").insert(base);
    if (fallbackError) throw fallbackError;
    return;
  }
  throw error;
}

// Aggregates { avg, count } per product_id from admin-approved reviews —
// used to show a trust-signal star rating on the Products page. Reviews
// submitted before product attribution existed (product_id null) simply
// don't count toward any product, rather than being dropped entirely.
export function useProductRatings() {
  const reviews = useVisibleReviews();
  return useMemo(() => {
    const map = {};
    (reviews || []).forEach((r) => {
      if (!r.product_id) return;
      if (!map[r.product_id]) map[r.product_id] = { total: 0, count: 0 };
      map[r.product_id].total += r.rating;
      map[r.product_id].count += 1;
    });
    const out = {};
    Object.entries(map).forEach(([id, { total, count }]) => {
      out[id] = { avg: total / count, count };
    });
    return out;
  }, [reviews]);
}

// Public, admin-approved reviews for display on the website (no auth needed).
export function useVisibleReviews() {
  const [list, setList] = useState(null);

  useEffect(() => {
    if (!supabaseReady) { setList([]); return; }

    supabase
      .from("reviews")
      .select("*")
      .eq("visible", true)
      .order("created_at", { ascending: false })
      .then(({ data }) => setList((data || []).map(normalizeTimestamped)));

    const channel = supabase
      .channel(ch("visible-reviews"))
      .on("postgres_changes", { event: "*", schema: "public", table: "reviews" }, () => {
        // Visibility/rows changed — simplest correct approach is a refetch.
        supabase
          .from("reviews")
          .select("*")
          .eq("visible", true)
          .order("created_at", { ascending: false })
          .then(({ data }) => setList((data || []).map(normalizeTimestamped)));
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return list;
}

// All reviews for the admin Reviews tab.
export function useAllReviews() {
  const [list, setList] = useState(null);

  useEffect(() => {
    if (!supabaseReady) { setList([]); return; }

    supabase
      .from("reviews")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data }) => setList((data || []).map(normalizeTimestamped)));

    const channel = supabase
      .channel(ch("all-reviews"))
      .on("postgres_changes", { event: "*", schema: "public", table: "reviews" }, (p) => {
        setList((prev) => {
          if (!prev) return prev;
          if (p.eventType === "INSERT") return [normalizeTimestamped(p.new), ...prev];
          if (p.eventType === "UPDATE") return prev.map((r) => r.id === p.new.id ? normalizeTimestamped(p.new) : r);
          if (p.eventType === "DELETE") return prev.filter((r) => r.id !== p.old.id);
          return prev;
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return list;
}

export async function setReviewVisibility(id, visible) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const { error } = await supabase.from("reviews").update({ visible }).eq("id", id);
  if (error) throw error;
}

export async function fetchOrderOnce(id) {
  if (!supabaseReady) return null;
  const { data, error } = await supabase.rpc("get_order_by_id", { p_id: id });
  return error || !data?.length ? null : normalizeOrder(data[0]);
}

// ─── OFFERS ──────────────────────────────────────────────────────────────────
function isOfferLive(o) {
  if (o.status !== "active") return false;
  if (o.expiry_date && new Date(o.expiry_date) < new Date(new Date().toDateString())) return false;
  return true;
}

// Admin: every offer, both scopes, active or not.
export function useAllOffers() {
  const [list, setList] = useState(null);

  useEffect(() => {
    if (!supabaseReady) { setList([]); return; }

    supabase
      .from("offers")
      .select("*")
      .order("created_at", { ascending: false })
      .then(({ data }) => setList((data || []).map(normalizeTimestamped)));

    const channel = supabase
      .channel(ch("all-offers"))
      .on("postgres_changes", { event: "*", schema: "public", table: "offers" }, (p) => {
        setList((prev) => {
          if (!prev) return prev;
          if (p.eventType === "INSERT") return [normalizeTimestamped(p.new), ...prev];
          if (p.eventType === "UPDATE") return prev.map((o) => o.id === p.new.id ? normalizeTimestamped(p.new) : o);
          if (p.eventType === "DELETE") return prev.filter((o) => o.id !== p.old.id);
          return prev;
        });
      })
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return list;
}

// Public: active, unexpired site-wide offers — shown to every visitor.
export function useActiveSiteOffers() {
  const [list, setList] = useState([]);

  useEffect(() => {
    if (!supabaseReady) return;

    const load = () => supabase
      .from("offers")
      .select("*")
      .eq("scope", "site")
      .eq("status", "active")
      .then(({ data }) => setList((data || []).map(normalizeTimestamped).filter(isOfferLive)));
    load();

    const channel = supabase
      .channel(ch("site-offers"))
      .on("postgres_changes", { event: "*", schema: "public", table: "offers" }, load)
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, []);

  return list;
}

// A specific logged-in customer's own active, unexpired personal offers.
export function useMyActiveOffers(uid) {
  const [list, setList] = useState([]);

  useEffect(() => {
    if (!supabaseReady || !uid) { setList([]); return; }

    const load = () => supabase
      .from("offers")
      .select("*")
      .eq("scope", "user")
      .eq("customer_id", uid)
      .eq("status", "active")
      .then(({ data }) => setList((data || []).map(normalizeTimestamped).filter(isOfferLive)));
    load();

    const channel = supabase
      .channel(ch(`my-offers-${uid}`))
      .on("postgres_changes", {
        event: "*", schema: "public", table: "offers", filter: `customer_id=eq.${uid}`,
      }, load)
      .subscribe();

    return () => supabase.removeChannel(channel);
  }, [uid]);

  return list;
}

// offer.id starting with "temp-" means it only exists in the admin's local
// draft (added via "+ Add Row", never saved) — insert instead of update.
export async function saveOffer(offer) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  const isNew = !offer.id || offer.id.startsWith("temp-");
  const payload = {
    scope: offer.scope,
    customer_id: offer.scope === "user" ? (offer.customer_id || null) : null,
    customer_email: offer.scope === "user" ? (offer.customer_email || null) : null,
    employee_name: offer.scope === "employee" ? (offer.employee_name || null) : null,
    name: offer.name,
    code: (offer.code || "").trim().toUpperCase() || null,
    discount_percent: Number(offer.discount_percent) || 0,
    expiry_date: offer.expiry_date || null,
    usage_limit: offer.usage_limit === "" || offer.usage_limit == null ? null : Number(offer.usage_limit),
    status: offer.status || "active",
  };
  if (isNew) {
    const { data, error } = await supabase.from("offers").insert(payload).select().single();
    if (error) throw error;
    return normalizeTimestamped(data);
  } else {
    const { data, error } = await supabase.from("offers").update(payload).eq("id", offer.id).select().single();
    if (error) throw error;
    return normalizeTimestamped(data);
  }
}

export async function deleteOffer(id) {
  if (!supabaseReady) throw new Error("Supabase not configured");
  if (id.startsWith("temp-")) return; // never persisted, nothing to delete
  const { error } = await supabase.from("offers").delete().eq("id", id);
  if (error) throw error;
}

// Read-only check — safe to call repeatedly while the customer is typing a
// code, or right after picking one from the "available offers" dropdown.
// Returns the offer (so the caller can show the discount %) or null.
// opts.employeeReferralUsed lets the caller pass along the signed-in
// customer's own profile.employee_referral_used_at (already loaded via
// AuthContext, so no extra query needed here) — checked client-side for a
// specific, friendly message; the real enforcement is server-side in
// _price_checkout_items at order-creation time regardless of this check.
export async function validateOfferCode(code, uid, opts = {}) {
  if (!supabaseReady || !code?.trim()) return null;
  const { data, error } = await supabase.rpc("validate_offer_code", { p_code: code.trim(), p_uid: uid || null });
  if (error) throw error;
  const offer = data?.[0] ? normalizeTimestamped(data[0]) : null;
  if (!offer) return null;
  // Employee-referral codes are never listed to customers (dropdown only
  // ever shows site/user offers) — they can only be typed in manually, and
  // only by a signed-in customer who hasn't redeemed one before.
  if (offer.scope === "employee") {
    if (!uid) throw new Error("Please sign in to use this referral code.");
    if (opts.employeeReferralUsed) throw new Error("You've already used an employee referral code.");
  }
  // Checked here (not filtered out by the RPC) so this gets its own message
  // instead of being lumped in with "wrong code" / "expired".
  if (offer.usage_limit != null && offer.used_count >= offer.usage_limit) {
    throw new Error("This offer's usage limit has been reached.");
  }
  return offer;
}

// Call once, right after an order using this code is successfully created.
export async function redeemOfferCode(code) {
  if (!supabaseReady || !code) return;
  const { error } = await supabase.rpc("redeem_offer_code", { p_code: code });
  if (error) throw error;
}
